import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from '../auth/useAuth';

// ============================================================
// Scope & Requirements Page
// ============================================================

// ── Types ─────────────────────────────────────────────────────
interface ScopeItem {
  ScopeItemID:    number;
  ProjectID:      number;
  Category:       string | null;
  Question:       string;
  VendorResponse: string | null;
  Status:         'Open' | 'Awaiting' | 'Answered' | 'Resolved' | 'NA';
  IsRequired:     boolean;
  AddedByName:    string | null;
  ResolvedAt:     string | null;
  CreatedAt:      string;
}

interface ProjectOption {
  ProjectID:      number;
  Title:          string;
  DepartmentName: string;
  ApprovalStatus: string;
  RequestorEmail: string;
}

// ── Help content ──────────────────────────────────────────────
const HELP_CONTENT: Record<string, { title: string; body: string; good?: string; bad?: string }> = {
  scope: {
    title: 'What is Scope & Requirements?',
    body: 'This page tracks specific questions and requirements for a project — especially when evaluating a vendor or software solution. Each item is a question that needs an answer, or a requirement the solution must meet.',
    good: '"Must support exporting reports to PDF and Excel."',
    bad: '"Reporting should be good."',
  },
  category: {
    title: 'What is a Category?',
    body: 'Categories help organize requirements into groups so they\'re easier to review. Common categories include: Functional (what the system does), Technical (how it works), Security (how data is protected), and Integration (how it connects to other systems).',
    good: 'Security, Functional, Integration, Accessibility',
    bad: 'Stuff, Misc, Other',
  },
  requirement: {
    title: 'Writing a Good Requirement',
    body: 'A good requirement is specific and testable — you should be able to clearly say yes or no to whether it\'s met. Avoid vague language like "user-friendly" or "fast."',
    good: '"The system must load the dashboard in under 3 seconds on a standard internet connection."',
    bad: '"The system should be fast and easy to use."',
  },
  status: {
    title: 'Understanding Status',
    body: [
      '• Open — No response yet. This item needs attention.',
      '• Awaiting — We\'ve asked the vendor. Waiting for their reply.',
      '• Answered — The vendor has responded. IT needs to review.',
      '• Resolved — Reviewed and accepted. This requirement is satisfied.',
      '• NA — Not applicable to this project.',
    ].join('\n'),
  },
  required: {
    title: 'Required vs. Nice-to-Have',
    body: 'A required item is something the solution MUST have — without it, we cannot move forward. A nice-to-have is something that would be helpful but isn\'t a dealbreaker.',
    good: 'Required: "Must integrate with Tyler EPL for permit data."',
    bad: 'Nice-to-have: "Would be great if it had a mobile app."',
  },
};

// ── Help Modal ────────────────────────────────────────────────
function HelpModal({ topic, onClose }: { topic: string; onClose: () => void }) {
  const content = HELP_CONTENT[topic];
  if (!content) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', borderRadius: 12, padding: 28,
        maxWidth: 480, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{content.title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text2)' }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: content.good ? 14 : 0 }}>
          {content.body}
        </div>
        {content.good && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success-text, #065f46)', marginBottom: 4 }}>✅ Good example</div>
            <div style={{ fontSize: 12, background: 'var(--surface2)', borderRadius: 6, padding: '7px 12px', color: 'var(--text1)', marginBottom: 10 }}>{content.good}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger-text, #991b1b)', marginBottom: 4 }}>❌ Not helpful</div>
            <div style={{ fontSize: 12, background: 'var(--surface2)', borderRadius: 6, padding: '7px 12px', color: 'var(--text1)' }}>{content.bad}</div>
          </div>
        )}
        <button className="btn btn-primary" style={{ marginTop: 18, width: '100%' }} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

function HelpButton({ topic, onOpen }: { topic: string; onOpen: (t: string) => void }) {
  return (
    <button onClick={() => onOpen(topic)} style={{
      width: 18, height: 18, borderRadius: '50%', fontSize: 11, fontWeight: 700,
      background: 'var(--border2)', border: 'none', cursor: 'pointer',
      color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginLeft: 6, flexShrink: 0,
    }} title="Help">?</button>
  );
}

// ── Status badge ──────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    'Resolved': 'badge-success',
    'Answered': 'badge-info',
    'Awaiting': 'badge-warning',
    'Open':     'badge-neutral',
    'NA':       'badge-neutral',
  };
  return <span className={`badge ${map[status] || 'badge-neutral'}`}>{status}</span>;
}

// ── Add Item Form ─────────────────────────────────────────────
function AddItemForm({
  projectId,
  userName,
  onSave,
  onCancel,
  onOpenHelp,
}: {
  projectId: number;
  userName: string;
  onSave: () => void;
  onCancel: () => void;
  onOpenHelp: (t: string) => void;
}) {
  const [form, setForm] = useState({
    category:   '',
    question:   '',
    isRequired: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const CATEGORIES = ['Functional', 'Technical', 'Security', 'Integration', 'Accessibility', 'Reporting', 'Training', 'Other'];

  async function handleSave() {
    if (!form.question.trim()) { setError('Please enter a requirement or question.'); return; }
    setSaving(true);
    try {
      await api.scope.create(projectId, {
        category:    form.category || null,
        question:    form.question.trim(),
        isRequired:  form.isRequired ? 1 : 0,
        addedByName: userName,
      });
      onSave();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'var(--surface2, #f8fafc)', border: '1px solid var(--border)',
      borderRadius: 10, padding: 18, marginBottom: 16,
    }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, display: 'flex', alignItems: 'center' }}>
        Add Requirement or Question
        <HelpButton topic="requirement" onOpen={onOpenHelp} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            Category <HelpButton topic="category" onOpen={onOpenHelp} />
          </label>
          <select className="field-select" style={{ width: '100%' }}
            value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            <option value="">— Select category —</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            Priority <HelpButton topic="required" onOpen={onOpenHelp} />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {[true, false].map(val => (
              <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" checked={form.isRequired === val}
                  onChange={() => setForm(f => ({ ...f, isRequired: val }))} />
                {val ? '🔴 Required' : '🟡 Nice-to-have'}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          Requirement or Question <HelpButton topic="requirement" onOpen={onOpenHelp} />
        </label>
        <textarea
          className="field-input"
          style={{ width: '100%', minHeight: 80, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
          placeholder="e.g. Must integrate with Tyler EPL for permit data. — or — Does the system support bulk data import?"
          value={form.question}
          onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
        />
      </div>

      {error && <div style={{ color: 'var(--danger-text, #991b1b)', fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Requirement'}
        </button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Scope Item Row ────────────────────────────────────────────
function ScopeItemRow({
  item,
  canEdit,
  onOpenHelp,
  onUpdated,
}: {
  item: ScopeItem;
  canEdit: boolean;
  onOpenHelp: (t: string) => void;
  onUpdated: () => void;
}) {
  const [expanded, setExpanded]       = useState(false);
  const [response, setResponse]       = useState(item.VendorResponse || '');
  const [status, setStatus]           = useState(item.Status);
  const [saving, setSaving]           = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await api.scope.update(item.ScopeItemID, {
        vendorResponse: response || null,
        status,
      });
      onUpdated();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  const STATUSES = ['Open', 'Awaiting', 'Answered', 'Resolved', 'NA'];

  return (
    <div style={{
      borderBottom: '1px solid var(--border)',
      background: item.Status === 'Resolved' ? 'var(--surface2, #f8fafc)' : 'var(--surface)',
    }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px' }}>
        {/* Required indicator */}
        <div style={{
          width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0,
          background: item.IsRequired ? 'var(--danger, #ef4444)' : 'var(--warning, #f59e0b)',
        }} title={item.IsRequired ? 'Required' : 'Nice-to-have'} />

        {/* Content */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', marginBottom: 4, lineHeight: 1.5 }}>
            {item.Question}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {item.Category && <span>📁 {item.Category}</span>}
            {item.AddedByName && <span>👤 Added by {item.AddedByName}</span>}
            {item.IsRequired
              ? <span style={{ color: 'var(--danger, #ef4444)' }}>● Required</span>
              : <span style={{ color: 'var(--warning, #f59e0b)' }}>● Nice-to-have</span>}
          </div>

          {/* Vendor response preview */}
          {item.VendorResponse && (
            <div style={{
              fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)',
              borderRadius: 6, padding: '7px 12px', borderLeft: '2px solid var(--border2)',
              marginTop: 8,
            }}>
              <strong style={{ color: 'var(--text1)' }}>Response: </strong>{item.VendorResponse}
            </div>
          )}

          {/* Awaiting indicator */}
          {item.Status === 'Awaiting' && !item.VendorResponse && (
            <div style={{
              fontSize: 12, color: 'var(--warning-text, #92400e)',
              background: 'var(--warning-bg, #fffbeb)',
              borderRadius: 6, padding: '6px 12px',
              borderLeft: '2px solid var(--warning, #f59e0b)', marginTop: 8,
            }}>
              ⏳ Awaiting vendor response
            </div>
          )}

          {/* Expanded edit panel */}
          {expanded && canEdit && (
            <div style={{ marginTop: 12, padding: 14, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                  Status <HelpButton topic="status" onOpen={onOpenHelp} />
                </label>
                <select className="field-select" value={status}
                  onChange={e => setStatus(e.target.value as any)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>
                  Vendor Response / Answer
                </label>
                <textarea
                  className="field-input"
                  style={{ width: '100%', minHeight: 70, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
                  placeholder="Enter the vendor's response or your team's answer..."
                  value={response}
                  onChange={e => setResponse(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button className="btn btn-sm" onClick={() => setExpanded(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          <StatusBadge status={item.Status} />
          {canEdit && (
            <button className="btn btn-sm" onClick={() => setExpanded(e => !e)} style={{ fontSize: 11 }}>
              {expanded ? 'Close' : 'Edit / Respond'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export function ScopePage() {
  const { user, isAdmin } = useAuth();

  const [projects, setProjects]     = useState<ProjectOption[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [items, setItems]           = useState<ScopeItem[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingItems, setLoadingItems]       = useState(false);
  const [filter, setFilter]         = useState('All');
  const [showAdd, setShowAdd]       = useState(false);
  const [helpTopic, setHelpTopic]   = useState<string | null>(null);

  // Load projects
  useEffect(() => {
    api.projects.list()
      .then(all => {
        const opts = all as unknown as ProjectOption[];
        setProjects(opts);
        if (opts.length > 0) {
          // Default to user's own most recent project, or first
          const mine = opts.find(p => p.RequestorEmail === user?.email);
          setSelectedId(mine ? mine.ProjectID : opts[0].ProjectID);
        }
      })
      .finally(() => setLoadingProjects(false));
  }, [user?.email]);

  // Load scope items when project changes
  const loadItems = useCallback(() => {
    if (!selectedId) return;
    setLoadingItems(true);
    api.scope.list(selectedId)
      .then(data => setItems(data as unknown as ScopeItem[]))
      .finally(() => setLoadingItems(false));
  }, [selectedId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const canEdit = isAdmin;

  // Filter items
  const filtered = items.filter(item => {
    if (filter === 'All') return true;
    return item.Status === filter;
  });

  // Group by category
  const grouped = filtered.reduce<Record<string, ScopeItem[]>>((acc, item) => {
    const key = item.Category || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const resolvedCount = items.filter(i => i.Status === 'Resolved').length;

  const FILTERS = ['All', 'Open', 'Awaiting', 'Answered', 'Resolved'];

  return (
    <div>
      {helpTopic && <HelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />}

      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Requirements Tracker
              <HelpButton topic="scope" onOpen={setHelpTopic} />
            </h1>
           <p className="page-sub">Cross-project view of requirements and vendor responses — all projects</p>
          </div>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setShowAdd(s => !s)}>
            + Add Requirement
          </button>
        )}
      </div>

      {/* Project selector */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Project
          </label>
          {loadingProjects ? (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>Loading projects...</div>
          ) : (
            <select
              className="field-select"
              style={{ minWidth: 300 }}
              value={selectedId || ''}
              onChange={e => { setSelectedId(Number(e.target.value)); setShowAdd(false); }}
            >
              {projects.map(p => (
                <option key={p.ProjectID} value={p.ProjectID}>
                  {p.Title} — {p.DepartmentName} ({p.ApprovalStatus})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Progress indicator */}
        {items.length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 16 }}>
            <span style={{ color: 'var(--success-text, #065f46)', fontWeight: 600 }}>{resolvedCount}</span>
            {' of '}
            <span style={{ fontWeight: 600 }}>{items.length}</span>
            {' resolved'}
          </div>
        )}
      </div>

      {/* Add item form */}
      {showAdd && selectedId && (
        <AddItemForm
          projectId={selectedId}
          userName={user?.name || 'Unknown'}
          onSave={() => { setShowAdd(false); loadItems(); }}
          onCancel={() => setShowAdd(false)}
          onOpenHelp={setHelpTopic}
        />
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            fontSize: 12, padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
            border: '1px solid var(--border2)',
            background: filter === f ? 'var(--accent)' : 'var(--surface)',
            color: filter === f ? '#fff' : 'var(--text2)',
            fontWeight: filter === f ? 600 : 400,
          }}>{f}</button>
        ))}
      </div>

      {/* Items list */}
      {loadingItems ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Loading requirements...</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>No requirements yet</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            Requirements help track what this project needs to deliver and questions for vendors.
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add First Requirement</button>
          )}
          <button style={{ marginLeft: 10 }} className="btn" onClick={() => setHelpTopic('scope')}>
            Learn more
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
          No items match the selected filter.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Summary header */}
          <div style={{
            padding: '11px 18px', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 12, fontWeight: 600, color: 'var(--text2)',
          }}>
            <span>{filtered.length} requirement{filtered.length !== 1 ? 's' : ''}</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <span>🔴 {items.filter(i => i.IsRequired).length} required</span>
              <span>🟡 {items.filter(i => !i.IsRequired).length} nice-to-have</span>
              <span style={{ color: 'var(--success-text, #065f46)' }}>✅ {resolvedCount} resolved</span>
            </div>
          </div>

          {/* Grouped items */}
          {Object.entries(grouped).map(([category, categoryItems]) => (
            <div key={category}>
              <div style={{
                padding: '8px 18px', background: 'var(--surface2, #f8fafc)',
                fontSize: 11, fontWeight: 700, color: 'var(--text2)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                borderBottom: '1px solid var(--border)',
              }}>
                {category} ({categoryItems.length})
              </div>
              {categoryItems.map(item => (
                <ScopeItemRow
                  key={item.ScopeItemID}
                  item={item}
                  canEdit={canEdit}
                  onOpenHelp={setHelpTopic}
                  onUpdated={loadItems}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text2)', display: 'flex', gap: 16 }}>
        <span>🔴 Required item</span>
        <span>🟡 Nice-to-have item</span>
        <button onClick={() => setHelpTopic('status')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent)', padding: 0 }}>
          What do the statuses mean?
        </button>
      </div>
    </div>
  );
}
